<template>
  <div class="product-page data-governance">
    <!-- 页面标题和描述 -->
    <div class="page-header">
      <div class="container">
        <h1>{{ productData.title }}</h1>
        <p class="description">{{ productData.description }}</p>
      </div>
    </div>

    <!-- 合作伙伴 -->
    <section class="section partners-section">
      <div class="container">
        <h2 class="section-title">合作伙伴</h2>
        <div class="partners-grid">
          <div 
            v-for="(partner, index) in productData.partners" 
            :key="partner.name"
            class="partner-card"
            :style="{ animationDelay: `${index * 0.1}s` }"
          >
            <img :src="partner.logo" :alt="partner.name">
            <h3>{{ partner.name }}</h3>
          </div>
        </div>
      </div>
    </section>

    <!-- 平台介绍 -->
    <section class="section platform-section">
      <div class="container">
        <h2 class="section-title">数据智能平台</h2>
        <div class="platform-content">
          <div class="platform-info">
            <h3>MeinnDM 一站式数据智能平台</h3>
            <p>{{ productData.platformDesc }}</p>
            <div class="platform-preview">
              <img src="@/assets/images/4.png" alt="平台预览">
            </div>
            <div class="module-grid">
              <div 
                v-for="module in productData.modules" 
                :key="module.title"
                class="module-card"
              >
                <div class="icon-wrapper">
                  <el-icon class="module-icon" :size="32">
                    <component :is="module.icon" />
                  </el-icon>
                </div>
                <h4>{{ module.title }}</h4>
                <p>{{ module.description }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- 服务能力 -->
    <section class="section capabilities-section">
      <div class="container">
        <h2 class="section-title">服务能力</h2>
        <div class="capabilities-grid">
          <div 
            v-for="capability in productData.capabilities" 
            :key="capability.title"
            class="capability-card"
          >
            <div class="icon-wrapper">
              <el-icon class="capability-icon" :size="32">
                <component :is="capability.icon" />
              </el-icon>
            </div>
            <h3>{{ capability.title }}</h3>
            <p>{{ capability.description }}</p>
            <ul class="feature-list">
              <li v-for="feature in capability.features" :key="feature">{{ feature }}</li>
            </ul>
          </div>
        </div>
      </div>
    </section>

    <!-- 服务价值 -->
    <section class="section value-section">
      <div class="container">
        <h2 class="section-title">服务价值</h2>
        <div class="value-grid">
          <div 
            v-for="value in productData.values" 
            :key="value.title"
            class="value-card"
          >
            <div class="value-icon">
              <el-icon :size="32">
                <component :is="value.icon" />
              </el-icon>
            </div>
            <h3>{{ value.title }}</h3>
            <p>{{ value.description }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- 治理流程和核心功能 -->
    <section class="section features-section">
      <div class="container">
        <h2 class="section-title">治理流程与核心功能</h2>
        <div class="features-container">
          <!-- 治理流程 -->
          <div class="process-cards">
            <div 
              v-for="(step, index) in productData.process" 
              :key="step.title"
              class="process-card"
              :style="{ animationDelay: `${index * 0.1}s` }"
            >
              <div class="card-header">
                <div class="step-number">{{ index + 1 }}</div>
                <div class="step-icon">
                  <el-icon :size="24">
                    <component :is="step.icon" />
                  </el-icon>
                </div>
              </div>
              <h3>{{ step.title }}</h3>
              <p>{{ step.description }}</p>
            </div>
          </div>

          <!-- 核心功能 -->
          <div class="core-features">
            <div 
              v-for="(feature, index) in productData.coreFeatures" 
              :key="feature.title"
              class="feature-card"
              :style="{ animationDelay: `${index * 0.1}s` }"
            >
              <div class="feature-icon">
                <el-icon :size="28">
                  <component :is="feature.icon" />
                </el-icon>
              </div>
              <div class="feature-content">
                <h3>{{ feature.title }}</h3>
                <ul class="feature-list">
                  <li v-for="item in feature.items" :key="item">{{ item }}</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { 
  DataLine,
  Connection,
  Monitor,
  Setting,
  Platform,
  Service,
  Management,
  CircleCheck,
  DataAnalysis,
  Promotion,
  Operation
} from '@element-plus/icons-vue'

const currentTab = ref('platform')

const tabs = [
  { key: 'platform', label: '平台介绍', icon: Platform },
  { key: 'capabilities', label: '服务能力', icon: Service },
  { key: 'values', label: '服务价值', icon: DataAnalysis }
]

const productData = {
  title: '数据治理',
  description: '基于云原生、行业大模型等技术底座，湖南美音自主研发中小型一站式湖仓一体数据智能平台（MeinnDM），为客户提供数据治理、分析挖掘、微应用开发以及运维一体化的一站式全流程数据能力服务方案。',
  platformDesc: '数据智能平台（MeinnDM）包含数据集成、数据开发、数据治理、数据资产、数据服务、数据集市六大模块，解决数据孤岛问题，实现数据统一口径标准，自定义数据开发任务，帮助企业、政府等客户解决数据管理核心难点问题。',
  modules: [
    {
      icon: DataLine,
      title: '数据集成',
      description: '多源异构数据统一集成管理'
    },
    {
      icon: Connection,
      title: '数据开发',
      description: '灵活的数据处理与开发能力'
    },
    {
      icon: Monitor,
      title: '数据治理',
      description: '全方位数据质量管理与治理'
    },
    {
      icon: Platform,
      title: '数据资产',
      description: '完整的数据资产管理体系'
    },
    {
      icon: Service,
      title: '数据服务',
      description: '便捷的数据服务开放能力'
    },
    {
      icon: Setting,
      title: '数据集市',
      description: '专业的数据分析与展现'
    }
  ],
  capabilities: [
    {
      icon: DataAnalysis,
      title: '数据治理能力',
      description: '提供全面的数据治理服务',
      features: [
        '数据标准规范制定',
        '数据质量监控管理',
        '数据清洗与转换',
        '元数据统一管理'
      ]
    },
    {
      icon: Connection,
      title: '数据分析能力',
      description: '强大的数据分析挖掘能力',
      features: [
        '多维数据分析',
        '数据挖掘建模',
        '可视化分析展示',
        '智能分析报告'
      ]
    },
    {
      icon: Platform,
      title: '微应用开发',
      description: '灵活的应用开发能力',
      features: [
        '低代码开发平台',
        '数据服务接口',
        '应用模板库',
        '快速部署上线'
      ]
    }
  ],
  values: [
    {
      icon: Management,
      title: '数据价值提升',
      description: '充分挖掘数据价值，助力业务决策'
    },
    {
      icon: CircleCheck,
      title: '数据质量保障',
      description: '确保数据准确性、完整性和及时性'
    },
    {
      icon: Operation,
      title: '数据资产管理',
      description: '实现数据资产的统一管理与共享'
    },
    {
      icon: Promotion,
      title: '数据应用赋能',
      description: '支持多样化的数据应用场景'
    }
  ],
  process: [
    {
      title: '数据评估',
      description: '评估数据现状，识别问题和需求',
      icon: DataLine
    },
    {
      title: '标准制定',
      description: '建立数据标准和治理规范',
      icon: Connection
    },
    {
      title: '数据治理',
      description: '执行数据清洗、整合等治理工作',
      icon: Monitor
    },
    {
      title: '持续运营',
      description: '持续监控和优化数据质量',
      icon: Platform
    }
  ],
  coreFeatures: [
    {
      title: '数据标准化',
      items: ['标准化数据模型', '统一数据口径', '数据字典管理'],
      icon: DataLine
    },
    {
      title: '数据质量管理',
      items: ['数据清洗', '数据校验', '数据监控'],
      icon: Connection
    },
    {
      title: '数据资产管理',
      items: ['数据资产目录', '数据血缘分析', '数据权限管理'],
      icon: Monitor
    }
  ],
  partners: [
    { name: '合作伙伴A', logo: '/path/to/1.png' },
    { name: '合作伙伴B', logo: '/path/to/2.png' }
  ]
}
</script>

<style lang="scss" scoped>
@import '@/assets/styles/products.scss';

.data-governance {
  padding: 0;
}

.page-header {
  &::before {
    content: '';
    position: absolute;
    width: 100%;
    height: 100%;
    top: 0;
    left: 0;
    background-image: 
      linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
      linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px);
    background-size: 20px 20px;
    opacity: 0.1;
  }
}
</style> 